import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Home, Library, LayoutGrid } from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  setName?: string;
  missionName?: string;
  cardName?: string;
}

export function Breadcrumbs({ items, setName, missionName, cardName }: BreadcrumbsProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const controlNavbar = () => {
      if (typeof window !== 'undefined') {
        if (isHovering || window.scrollY < 10 || window.scrollY < lastScrollY) {
          setIsVisible(true);
        } else if (window.scrollY > lastScrollY) {
          setIsVisible(false);
        }
        setLastScrollY(window.scrollY);
      }
    };

    window.addEventListener('scroll', controlNavbar);
    
    // Détecter les interactions avec le header
    const header = document.querySelector('header');
    if (header) {
      const showNav = () => setIsVisible(true);
      header.addEventListener('mouseenter', showNav);
      header.addEventListener('click', showNav);

      return () => {
        window.removeEventListener('scroll', controlNavbar);
        header.removeEventListener('mouseenter', showNav);
        header.removeEventListener('click', showNav);
      };
    }

    return () => {
      window.removeEventListener('scroll', controlNavbar);
    };
  }, [lastScrollY, isHovering]);

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isCollectionPage = location.pathname.startsWith('/sets');
  const isMainSetsPage = location.pathname === '/sets';
  const showBackButton = !isMainSetsPage;

  // Ne pas afficher de breadcrumb sur les pages de choix
  const choicePages = ['pvp-choice', 'trades-choice', 'decks-choice', 'other-choice'];
  if (choicePages.includes(pathSegments[0])) {
    return null;
  }

  const handleGoBack = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(-1);
  };

  // Construire les items du breadcrumb en fonction des props
  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    if (items) return items;

    const breadcrumbs: BreadcrumbItem[] = [];
    
    if (setName && pathSegments[0] === 'sets') {
      breadcrumbs.push({
        label: setName,
        path: `/sets/${pathSegments[1]}`
      });
    }

    if (missionName) {
      breadcrumbs.push({
        label: missionName,
        path: `/sets/${pathSegments[1]}/missions/${pathSegments[3]}`
      });
    }

    if (cardName) {
      breadcrumbs.push({
        label: cardName,
        path: `/sets/${pathSegments[1]}/cards/${pathSegments[3]}`
      });
    }

    return breadcrumbs;
  };

  const breadcrumbItems = getBreadcrumbItems();

  if (!showBackButton && breadcrumbItems.length === 0) {
    return null;
  }

  return (
    <nav 
      className={`fixed top-16 left-0 right-0 z-10 bg-white border-b border-gray-200 transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center h-12">
          <div className="flex items-center w-full px-4 sm:px-6 lg:px-8">
            {showBackButton && (
              <>
                <button
                  onClick={handleGoBack}
                  className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="text-sm font-medium hidden sm:inline">Retour</span>
                </button>
                <div className="w-px h-5 bg-gray-200 mx-4" />
              </>
            )}

            <Link 
              to="/sets"
              className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors"
              aria-label="Collection"
            >
              {isCollectionPage ? (
                <LayoutGrid className="w-5 h-5" />
              ) : (
                <Home className="w-5 h-5" />
              )}
            </Link>
            
            {breadcrumbItems.length > 0 && (
              <div className="flex items-center min-w-0 ml-3">
                {breadcrumbItems.map((item, index) => (
                  <React.Fragment key={item.path}>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mx-1" />
                    <Link
                      to={item.path}
                      className="text-sm font-medium text-gray-600 hover:text-indigo-600 transition-colors whitespace-nowrap truncate max-w-[200px] flex items-center"
                    >
                      {index === 0 && <Library className="w-4 h-4 mr-1.5 flex-shrink-0" />}
                      {item.label}
                    </Link>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}